import { useEffect, useRef, useState } from "react";
import { app, pages } from "@microsoft/teams-js";
import { initAuth } from "./auth/teamsAuth";
import { adresseFuerBoard } from "./board";
import { ladeBoardsDerGruppe, type Board } from "./zeitleiste/laden";

// Diese Seite ruft Teams auf, wenn jemand den Tab zu einem Kanal hinzufügt.
// Sie zeigt die Planner-Boards des Teams, lässt eines wählen und schreibt
// die Wahl beim Speichern in die Inhaltsadresse des Tabs (?plan=…). Ab dann
// weiß der Tab dauerhaft, welches Jahr er zeigt – siehe board.ts.
//
// Teams-Vertrag: Erst registerOnSaveHandler, dann setValidityState(true) –
// und zwar erst, wenn wirklich ein Board gewählt ist. Sonst ließe sich ein
// Tab ohne Board speichern.

type Lage =
  | { art: "laedt" }
  | { art: "fehler"; text: string }
  | { art: "bereit"; teamName: string; boards: Board[] };

export function Config() {
  const [lage, setLage] = useState<Lage>({ art: "laedt" });
  const [auswahl, setAuswahl] = useState<Board | null>(null);
  // Der Speichern-Handler wird EINMAL registriert; die aktuelle Wahl holt
  // er sich aus dem Ref, sonst sähe er den Stand vom Zeitpunkt der
  // Registrierung (leer).
  const gewaehlt = useRef<Board | null>(null);
  const bereitsGestartet = useRef(false);

  useEffect(() => {
    if (bereitsGestartet.current) return;
    bereitsGestartet.current = true;
    (async () => {
      try {
        const { inTeams } = await initAuth();
        if (!inTeams) {
          setLage({
            art: "fehler",
            text: "Diese Seite läuft nur innerhalb von Teams – außerhalb fehlt der Kontext, den sie braucht.",
          });
          return;
        }

        const kontext = await app.getContext();
        const gruppenId = kontext.team?.groupId;
        if (!gruppenId) {
          setLage({
            art: "fehler",
            text: "Kein Team gefunden. Der Zeitplan lässt sich nur in einem Kanal eines Teams einrichten, nicht in einem Chat.",
          });
          return;
        }

        pages.config.registerOnSaveHandler((ereignis) => {
          const board = gewaehlt.current;
          if (!board) {
            ereignis.notifyFailure("Bitte zuerst ein Board wählen.");
            return;
          }
          const adresse = adresseFuerBoard(board.id);
          pages.config
            .setConfig({
              entityId: board.id,
              contentUrl: adresse,
              websiteUrl: adresse,
              suggestedDisplayName: "Zeitplan",
            })
            .then(() => ereignis.notifySuccess())
            .catch((e: unknown) => ereignis.notifyFailure(String(e)));
        });

        const boards = await ladeBoardsDerGruppe(gruppenId);
        setLage({ art: "bereit", teamName: kontext.team?.displayName ?? "dieses Team", boards });
      } catch (e) {
        setLage({ art: "fehler", text: e instanceof Error ? e.message : String(e) });
      }
    })();
  }, []);

  function waehle(board: Board) {
    gewaehlt.current = board;
    setAuswahl(board);
    // Erst jetzt darf Teams den Speichern-Knopf freigeben.
    void pages.config.setValidityState(true);
  }

  return (
    <main className="huelle">
      <h1>Zeitplan hinzufügen</h1>

      {lage.art === "laedt" && <p className="hinweis">Einen Moment …</p>}

      {lage.art === "fehler" && <p className="fehler">{lage.text}</p>}

      {lage.art === "bereit" && (
        <>
          <p>
            Welches Planner-Board soll dieser Tab zeigen? Zur Auswahl stehen die Boards von{" "}
            <strong>{lage.teamName}</strong>.
          </p>
          {lage.boards.length === 0 ? (
            <p className="hinweis">
              Dieses Team hat noch kein Planner-Board. Lege zuerst in Planner eines an und
              füge den Tab dann erneut hinzu.
            </p>
          ) : (
            <ul className="auswahl">
              {lage.boards.map((b) => (
                <li key={b.id}>
                  <label>
                    <input
                      type="radio"
                      name="board"
                      checked={auswahl?.id === b.id}
                      onChange={() => waehle(b)}
                    />{" "}
                    {b.title}
                  </label>
                </li>
              ))}
            </ul>
          )}
          <p className="hinweis">
            {auswahl
              ? `„${auswahl.title}“ gewählt. Unten rechts auf „Speichern“.`
              : "Wähle ein Board, dann lässt sich speichern."}
          </p>
        </>
      )}
    </main>
  );
}
